import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import {
  Settings as SettingsIcon,
  User,
  Shield,
  Bell,
  Database,
  Key,
  Server,
  Save,
  Eye,
  EyeOff,
  Zap
} from 'lucide-react';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  
  // Profile settings
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // System settings
  const [systemSettings, setSystemSettings] = useState({
    maxUsersPerPage: 20,
    maxTranscriptsPerPage: 20,
    defaultUserPlan: 'free',
    maxFileSize: 10,
    sessionTimeout: 30,
    enableEmailNotifications: true,
    enableAuditLogs: true,
    enableRateLimiting: true
  });

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'system', label: 'System', icon: Server },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ];

  const handleProfileSave = async () => {
    try {
      setLoading(true);
      
      // Validate password change
      if (profileData.newPassword) {
        if (!profileData.currentPassword) {
          toast.error('Current password is required to change password');
          return;
        }
        if (profileData.newPassword !== profileData.confirmPassword) {
          toast.error('New passwords do not match');
          return;
        }
        if (profileData.newPassword.length < 6) {
          toast.error('Password must be at least 6 characters');
          return;
        }
      }

      // Here you would make API calls to update the profile
      // For now, we'll just show a success message
      toast.success('Profile updated successfully');
      
      // Clear password fields
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSave = async () => {
    try {
      setLoading(true);
      
      // Here you would make API calls to update system settings
      // For now, we'll just show a success message
      toast.success('System settings updated successfully');
      
    } catch (error) {
      console.error('System settings update error:', error);
      toast.error('Failed to update system settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIndexes = async () => {
    if (!window.confirm('This will create database indexes to improve performance. This is safe and recommended. Continue?')) {
      return;
    }
    
    try {
      setLoading(true);
      toast.info('Creating database indexes... This may take a few minutes.');
      
      const response = await api.post('/admin/create-indexes');
      
      if (response.data.success) {
        toast.success('Database indexes created successfully! Statistics should load much faster now.');
      } else {
        toast.error('Failed to create some indexes. Check console for details.');
      }
      
      console.log('Index creation results:', response.data);
      
    } catch (error) {
      console.error('Create indexes error:', error);
      toast.error('Failed to create database indexes: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              type="text"
              className="input"
              value={profileData.name}
              onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              className="input"
              value={profileData.email}
              onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                className="input pr-10"
                value={profileData.currentPassword}
                onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
              >
                {showPasswords.current ? 
                  <EyeOff className="h-4 w-4 text-gray-400" /> : 
                  <Eye className="h-4 w-4 text-gray-400" />
                }
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                className="input pr-10"
                value={profileData.newPassword}
                onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
              >
                {showPasswords.new ? 
                  <EyeOff className="h-4 w-4 text-gray-400" /> : 
                  <Eye className="h-4 w-4 text-gray-400" />
                }
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                className="input pr-10"
                value={profileData.confirmPassword}
                onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
              >
                {showPasswords.confirm ? 
                  <EyeOff className="h-4 w-4 text-gray-400" /> : 
                  <Eye className="h-4 w-4 text-gray-400" />
                }
              </button>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-gray-500 mt-2">
          Leave password fields empty if you don't want to change your password.
        </p>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleProfileSave}
          disabled={loading}
          className="btn-primary"
        >
          {loading ? (
            <>
              <div className="loading-spinner w-4 h-4 mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderSystemTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">General Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Users Per Page
            </label>
            <input
              type="number"
              className="input"
              min="10"
              max="100"
              value={systemSettings.maxUsersPerPage}
              onChange={(e) => setSystemSettings(prev => ({ 
                ...prev, 
                maxUsersPerPage: parseInt(e.target.value) 
              }))}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transcripts Per Page
            </label>
            <input
              type="number"
              className="input"
              min="10"
              max="100"
              value={systemSettings.maxTranscriptsPerPage}
              onChange={(e) => setSystemSettings(prev => ({ 
                ...prev, 
                maxTranscriptsPerPage: parseInt(e.target.value) 
              }))}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default User Plan
            </label>
            <select
              className="input"
              value={systemSettings.defaultUserPlan}
              onChange={(e) => setSystemSettings(prev => ({ 
                ...prev, 
                defaultUserPlan: e.target.value 
              }))}
            >
              <option value="guest">Guest</option>
              <option value="free">Free</option>
              <option value="basic">Basic</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max File Size (MB)
            </label>
            <input
              type="number"
              className="input"
              min="1"
              max="100"
              value={systemSettings.maxFileSize}
              onChange={(e) => setSystemSettings(prev => ({ 
                ...prev, 
                maxFileSize: parseInt(e.target.value) 
              }))}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session Timeout (minutes)
            </label>
            <input
              type="number"
              className="input"
              min="15"
              max="480"
              value={systemSettings.sessionTimeout}
              onChange={(e) => setSystemSettings(prev => ({ 
                ...prev, 
                sessionTimeout: parseInt(e.target.value) 
              }))}
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Security & Features</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Email Notifications</h4>
              <p className="text-sm text-gray-500">Send email notifications for important events</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={systemSettings.enableEmailNotifications}
                onChange={(e) => setSystemSettings(prev => ({ 
                  ...prev, 
                  enableEmailNotifications: e.target.checked 
                }))}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Audit Logs</h4>
              <p className="text-sm text-gray-500">Keep detailed logs of all admin actions</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={systemSettings.enableAuditLogs}
                onChange={(e) => setSystemSettings(prev => ({ 
                  ...prev, 
                  enableAuditLogs: e.target.checked 
                }))}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Rate Limiting</h4>
              <p className="text-sm text-gray-500">Enable API rate limiting for user requests</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={systemSettings.enableRateLimiting}
                onChange={(e) => setSystemSettings(prev => ({ 
                  ...prev, 
                  enableRateLimiting: e.target.checked 
                }))}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Database Optimization</h3>
        
        <div className="card bg-blue-50 border-blue-200">
          <div className="card-body">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Create Database Indexes</h4>
                <p className="text-sm text-gray-600 mb-3">
                  If statistics are loading slowly or timing out, create database indexes to dramatically improve performance. 
                  This is safe and recommended for production environments with large datasets.
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  <strong>What this does:</strong> Creates indexes on frequently queried fields (createdAt, isActive, role, plan, status, userId) 
                  which makes database queries 100x faster.
                </p>
                <button
                  onClick={handleCreateIndexes}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner w-4 h-4 mr-2"></div>
                      Creating Indexes...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Create Indexes Now
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSystemSave}
          disabled={loading}
          className="btn-primary"
        >
          {loading ? (
            <>
              <div className="loading-spinner w-4 h-4 mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Information</h3>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">Admin Account Security</h4>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Use a strong, unique password for your admin account</li>
                  <li>Enable two-factor authentication when available</li>
                  <li>Regularly review admin activity logs</li>
                  <li>Log out from shared or public computers</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Security Events</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center">
              <Key className="w-5 h-5 text-green-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Successful login</p>
                <p className="text-xs text-gray-500">Today at 10:30 AM</p>
              </div>
            </div>
            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
              Success
            </span>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center">
              <Key className="w-5 h-5 text-green-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Successful login</p>
                <p className="text-xs text-gray-500">Yesterday at 3:45 PM</p>
              </div>
            </div>
            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
              Success
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h3>
        
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4">Email Notifications</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">New User Registrations</p>
                  <p className="text-sm text-gray-500">Get notified when new users sign up</p>
                </div>
                <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">System Errors</p>
                  <p className="text-sm text-gray-500">Get notified about critical system errors</p>
                </div>
                <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" defaultChecked />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Daily Reports</p>
                  <p className="text-sm text-gray-500">Receive daily usage and activity reports</p>
                </div>
                <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your account and system preferences
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar */}
        <div className="lg:w-64">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-left
                    ${activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon className={`
                    mr-3 h-5 w-5 flex-shrink-0
                    ${activeTab === tab.id ? 'text-primary-600' : 'text-gray-400'}
                  `} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="card">
            <div className="card-body">
              {activeTab === 'profile' && renderProfileTab()}
              {activeTab === 'security' && renderSecurityTab()}
              {activeTab === 'system' && renderSystemTab()}
              {activeTab === 'notifications' && renderNotificationsTab()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;